import { displayFromCodeableConcept } from '@seen/core/codeableConcept'
import type { ScaleType } from '@seen/core/codings'
import { referenceDisplay } from '@seen/core/display'
import type { ExtendedObservation } from '@seen/core/models/observation'
import { parseReferenceString } from '@seen/core/utils'
import type { JSX, MouseEventHandler, ReactNode } from 'react'
import { match } from 'ts-pattern'
import { ReferenceNameplate } from '#src/components/reference-picker/ReferenceNameplate.tsx'
import { ObservationWrapper } from '#src/components/rich-text-editor/ObservationNode/ObservationWrapper.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { extractBlockLinesFromHTML } from '#src/utils/html.ts'

export function ObservationDisplay({
  observation,
  codingDisplay,
  scaleType,
  onClick,
  noHover,
}: {
  observation: ExtendedObservation | undefined
  codingDisplay: string
  scaleType: ScaleType | null
  onClick?: MouseEventHandler<HTMLSpanElement>
  noHover?: boolean
}): JSX.Element {
  return (
    <ObservationWrapper className="pr-1" onClick={onClick} noHover={noHover}>
      <span>{codingDisplay}:&nbsp;</span>
      <ObservationValueDisplay
        observation={observation}
        scaleType={scaleType}
      />
    </ObservationWrapper>
  )
}

// A recorded observation's value and note, read-only, for a line that
// supplies its own box and label.
export function ObservationValueDisplay({
  observation,
  scaleType,
  noteOnOneLine = false,
}: {
  observation: ExtendedObservation | undefined
  scaleType: ScaleType | null
  // Shows only the note's first line, cut to a width, as in a note's text.
  noteOnOneLine?: boolean
}): JSX.Element {
  const value = match(scaleType)
    .with('Qn', () => (
      <ObservationNumberValue
        observation={observation}
        noteOnOneLine={noteOnOneLine}
      />
    ))
    .with('Ord', 'Nom', () => (
      <ObservationChoiceValue
        observation={observation}
        noteOnOneLine={noteOnOneLine}
      />
    ))
    .with('Nar', () => <ObservationTextValue observation={observation} />)
    .with('Ref', () => (
      <ObservationReferenceValue
        observation={observation}
        noteOnOneLine={noteOnOneLine}
      />
    ))
    .with(null, () => {
      throw new Error('scaleType is required')
    })
    .exhaustive()

  if (!observation) {
    return (
      <span className="animate-pulse text-foreground-tertiary">loading…</span>
    )
  }
  if (observation.status === 'entered-in-error') {
    return <span className="text-foreground-secondary">deleted</span>
  }
  // Components are shown whatever the scale type.
  if (observation.hasComponents) {
    return (
      <WithNote observation={observation} noteOnOneLine={noteOnOneLine}>
        {observation.componentDisplay ?? 'no value'}
      </WithNote>
    )
  }
  return value
}

function ObservationNumberValue({
  observation,
  noteOnOneLine,
}: {
  observation: ExtendedObservation | undefined
  noteOnOneLine: boolean
}): JSX.Element {
  // A date code is `Qn` too; its value is a day or a timestamp.
  if (observation?.valueDateTime) {
    return (
      <WithNote observation={observation} noteOnOneLine={noteOnOneLine}>
        <span className="text-blue-600">{observation.valueDisplay}</span>
      </WithNote>
    )
  }
  return (
    <WithNote observation={observation} noteOnOneLine={noteOnOneLine}>
      {observation?.valueQuantity ?? 'no value'}
      {observation?.valueUnit && (
        <span className="text-foreground-secondary">
          &nbsp;{observation.valueUnit}
        </span>
      )}
    </WithNote>
  )
}

function ObservationChoiceValue({
  observation,
  noteOnOneLine,
}: {
  observation: ExtendedObservation | undefined
  noteOnOneLine: boolean
}): JSX.Element {
  return (
    <WithNote observation={observation} noteOnOneLine={noteOnOneLine}>
      <span className="text-blue-600">
        {displayFromCodeableConcept(observation?.valueCodeableConcept) ??
          'no value'}
      </span>
    </WithNote>
  )
}

function ObservationReferenceValue({
  observation,
  noteOnOneLine,
}: {
  observation: ExtendedObservation | undefined
  noteOnOneLine: boolean
}): JSX.Element {
  const focus = observation?.focusReference
  const resourceType = parseReferenceString(focus?.reference)?.type
  return (
    <WithNote observation={observation} noteOnOneLine={noteOnOneLine}>
      <span className="text-blue-600">
        {focus?.reference && resourceType ? (
          <ReferenceNameplate
            resourceType={resourceType}
            reference={focus.reference}
          />
        ) : (
          (referenceDisplay(focus) ?? 'no value')
        )}
      </span>
    </WithNote>
  )
}

// Text from the note editor is HTML; text recorded elsewhere may be plain,
// its line breaks as newlines, which an HTML render would run together.
function ObservationTextValue({
  observation,
}: {
  observation: ExtendedObservation | undefined
}): JSX.Element {
  const text = observation?.valueString ?? 'no value'
  return HTML_TAG.test(text) ? (
    <RichText value={text} />
  ) : (
    <span className="whitespace-pre-wrap">{text}</span>
  )
}

const HTML_TAG = /<[a-z][^>]*>/i

function WithNote({
  observation,
  noteOnOneLine,
  children,
}: {
  observation: ExtendedObservation | undefined
  noteOnOneLine: boolean
  children: ReactNode
}): JSX.Element {
  const note = observation?.noteFirstText
  const lines = noteOnOneLine && note ? extractBlockLinesFromHTML(note) : []
  return (
    <>
      {children}
      {note && (
        <>
          &nbsp;&nbsp;
          {noteOnOneLine ? (
            <span className="max-w-[18rem] min-w-0 truncate">
              {lines[0]}
              {lines.length > 1 && '…'}
            </span>
          ) : (
            <RichText value={note} />
          )}
        </>
      )}
    </>
  )
}
